import { IS_NATIVE_APP, IS_ANDROID_APP } from "../../config/appTarget.js";
import {
  getPushPermissionPrompted,
  setPushPermissionPrompted,
} from "../appStorage.js";
import { dispatchNotificationReceived } from "../../features/notifications/events.js";
import { resolveNotificationPath } from "../../features/notifications/deepLink.js";

// FCM push wrapper built on @capacitor-firebase/messaging, which returns real
// FCM registration tokens on BOTH iOS and Android — what our backend's FCM HTTP
// v1 sender needs (@capacitor/push-notifications only yields raw APNs tokens on
// iOS). Everything is dynamically imported and native-guarded so the web bundle
// never pulls in the plugin.
//
// Permission is requested CONTEXTUALLY (after the user's first like), never on
// first launch. See docs/mobile/WEB_VS_ANDROID.md and WEB_VS_IOS.md.

type ApiCall = (url: string, options?: RequestInit) => Promise<Response>;
type Navigate = (path: string) => void;

let listenersRegistered = false;
let pendingToken: string | null = null;
let registerApiCall: ApiCall | null = null;

function platform(): "android" | "ios" {
  return IS_ANDROID_APP ? "android" : "ios";
}

/** POST the FCM token to the backend (de-dupe handled server-side by upsert). */
async function persistToken(token: string, apiCall: ApiCall): Promise<void> {
  try {
    // Report the device timezone so per-user quiet hours work (ignored server
    // side when absent/invalid, falling back to Europe/Lisbon in the worker).
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    await apiCall("/api/notifications/device-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, platform: platform(), timezone }),
    });
  } catch (err) {
    console.error("[push] failed to persist device token", err);
  }
}

/** Send an FCM token now if auth is ready, otherwise queue it for later. */
function handleToken(token: string | null | undefined): void {
  if (!token) return;
  if (registerApiCall) void persistToken(token, registerApiCall);
  else pendingToken = token;
}

/**
 * Register OS-level listeners exactly once: token registration/refresh and the
 * tapped-notification deep link. Safe to call on every mount; no-ops off native.
 */
export async function registerPushListeners({
  apiCall,
  navigate,
}: {
  apiCall: ApiCall;
  navigate: Navigate;
}): Promise<void> {
  registerApiCall = apiCall;

  // If a token arrived before auth was ready, flush it now.
  if (pendingToken) {
    await persistToken(pendingToken, apiCall);
    pendingToken = null;
  }

  if (!IS_NATIVE_APP || listenersRegistered) return;
  listenersRegistered = true;

  const { FirebaseMessaging, Importance } = await import(
    "@capacitor-firebase/messaging"
  );

  if (IS_ANDROID_APP) {
    try {
      // Id must match ANDROID_CHANNEL_ID in src/backend/lib/notifications/fcm.ts.
      await FirebaseMessaging.createChannel({
        id: "pawseum_default",
        name: "Pawseum",
        importance: Importance.High,
        vibration: true,
      });
    } catch (err) {
      console.error("[push] channel creation failed", err);
    }
  }

  // FCM token issued or refreshed.
  await FirebaseMessaging.addListener("tokenReceived", (event) => {
    handleToken(event.token);
  });

  // Notification arriving while the app is in the foreground: the OS does NOT
  // show a system banner, so surface an in-app banner and bump the bell.
  await FirebaseMessaging.addListener("notificationReceived", (event) => {
    const n = event.notification;
    dispatchNotificationReceived({
      title: n?.title ?? "",
      body: n?.body ?? "",
      data: (n?.data as Record<string, string>) ?? {},
    });
  });

  // Tapped notification -> deep-link to the relevant screen.
  await FirebaseMessaging.addListener(
    "notificationActionPerformed",
    (event) => {
      const data = (event.notification?.data as Record<string, string>) ?? {};
      // Fire-and-forget the open-rate metric (worker attaches `eventId`).
      if (data.eventId && registerApiCall) {
        void registerApiCall(
          `/api/notifications/events/${data.eventId}/opened`,
          { method: "POST" },
        ).catch(() => {});
      }
      const path = resolveNotificationPath(data);
      if (path) navigate(path);
    },
  );

  // If permission was already granted on a previous launch, pull the current
  // token now (the tokenReceived listener won't necessarily re-fire).
  try {
    const perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive === "granted") {
      const { token } = await FirebaseMessaging.getToken();
      handleToken(token);
    }
  } catch (err) {
    console.error("[push] token fetch failed", err);
  }
}

/**
 * Contextual permission prompt + registration. Call after the user's first
 * like. Idempotent: prompts at most once per device. No-op off native.
 */
export async function promptForPushPermission(
  apiCall: ApiCall,
): Promise<void> {
  if (!IS_NATIVE_APP) return;
  registerApiCall = apiCall;

  if (await getPushPermissionPrompted()) return;

  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");

  try {
    let status = await FirebaseMessaging.checkPermissions();
    if (
      status.receive === "prompt" ||
      status.receive === "prompt-with-rationale"
    ) {
      status = await FirebaseMessaging.requestPermissions();
    }
    // Only latch AFTER a real permission interaction has completed — if the
    // native plugin/import throws (e.g. the pod isn't installed) we must NOT
    // latch, or the prompt is permanently suppressed even after a fixed build.
    await setPushPermissionPrompted(true);
    if (status.receive !== "granted") return;

    // Returns the FCM registration token (iOS + Android).
    const { token } = await FirebaseMessaging.getToken();
    handleToken(token);
  } catch (err) {
    console.error("[push] permission/registration failed", err);
  }
}

export type PushPermissionState = "granted" | "denied" | "prompt" | "unavailable";

/** Current OS-level push permission. "unavailable" off native or on plugin error. */
export async function getPushPermissionState(): Promise<PushPermissionState> {
  if (!IS_NATIVE_APP) return "unavailable";
  try {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    const { receive } = await FirebaseMessaging.checkPermissions();
    if (receive === "granted" || receive === "denied") return receive;
    return "prompt";
  } catch {
    return "unavailable";
  }
}

/**
 * User-initiated enable (settings screen): asks the OS even if the contextual
 * prompt already ran, then registers the token. Returns the resulting state.
 */
export async function enablePushNotifications(
  apiCall: ApiCall,
): Promise<PushPermissionState> {
  if (!IS_NATIVE_APP) return "unavailable";
  registerApiCall = apiCall;
  try {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    let { receive } = await FirebaseMessaging.checkPermissions();
    if (receive === "prompt" || receive === "prompt-with-rationale") {
      ({ receive } = await FirebaseMessaging.requestPermissions());
    }
    await setPushPermissionPrompted(true);
    if (receive !== "granted") return receive === "denied" ? "denied" : "prompt";
    const { token } = await FirebaseMessaging.getToken();
    if (token) await persistToken(token, apiCall);
    return "granted";
  } catch (err) {
    console.error("[push] enable failed", err);
    return "unavailable";
  }
}
